import { useCallback } from "react";

import { Button, Modal, Textarea } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { generateAutomationName } from "app/pages/activation/helpers/generateAutomationName";

import { INSIGHT_NEGATIVE_FEEDBACK_MODAL_CUSTOM_STYLES } from "../constants";
import {
    INSIGHTS_FEEDBACK_MODAL_CANCEL,
    INSIGHTS_FEEDBACK_MODAL_PLACEHOLDER,
    INSIGHTS_FEEDBACK_MODAL_SUBMIT,
    INSIGHTS_FEEDBACK_MODAL_TITLE,
} from "../texts";

type InsightNegativeFeedbackModalProps = {
    isOpen: boolean;
    insightRowIndex: number;
    comment: string;
    onCommentChange: (value: string) => void;
    onClose: () => void;
    onSubmit: () => void;
    isSubmitting: boolean;
};

export const InsightNegativeFeedbackModal = ({
    isOpen,
    insightRowIndex,
    comment,
    onCommentChange,
    onClose,
    onSubmit,
    isSubmitting,
}: InsightNegativeFeedbackModalProps) => {
    const baseAutomation = generateAutomationName("home", [
        "insights",
        "row",
        String(insightRowIndex),
        "feedback",
        "modal",
    ]);

    const handleSubmit = useCallback(() => {
        if (!comment.trim()) {
            return;
        }

        onSubmit();
    }, [comment, onSubmit]);

    const canSubmit = comment.trim().length > 0;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={INSIGHT_NEGATIVE_FEEDBACK_MODAL_CUSTOM_STYLES}
            data-automation={baseAutomation}
        >
            <Modal.Header>
                <Modal.Title>{translate(INSIGHTS_FEEDBACK_MODAL_TITLE)}</Modal.Title>
            </Modal.Header>

            <Modal.Content>
                <div data-automation={`${baseAutomation}-textarea`}>
                    <Textarea
                        value={comment}
                        onChange={onCommentChange}
                        placeholder={translate(INSIGHTS_FEEDBACK_MODAL_PLACEHOLDER)}
                        height="147px"
                    />
                </div>
            </Modal.Content>

            <Modal.Footer>
                <Flex
                    width="100%"
                    justifyContent="flex-end"
                    gap="4px"
                >
                    <Button
                        type="flat"
                        dataAutomation={`${baseAutomation}-cancel`}
                        onClick={onClose}
                    >
                        {translate(INSIGHTS_FEEDBACK_MODAL_CANCEL)}
                    </Button>
                    <Button
                        type="primary"
                        dataAutomation={`${baseAutomation}-submit`}
                        isDisabled={!canSubmit}
                        isLoading={isSubmitting}
                        onClick={handleSubmit}
                    >
                        {translate(INSIGHTS_FEEDBACK_MODAL_SUBMIT)}
                    </Button>
                </Flex>
            </Modal.Footer>
        </Modal>
    );
};
